import React, { useCallback, useState } from 'react';
import {
  View,
  ScrollView,
  StatusBar,
  ActivityIndicator,
  StyleSheet,
  RefreshControl,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useRadius } from '@/hooks/shared/useRadius';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { FollowingUser } from '@/types/residents/marketplaceListing';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { UserAvatar } from '@/components/residents/marketplace/shared';
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { BackLink } from '@/components/residents/mobile/BackLink';
import { useGetFollowingQuery, useToggleFollowMutation } from '@/store/api';
import {
  GradientBackground,
  PageHeader,
  GlassCard,
} from '@/components/residents/redesign';

const ChevronRightIcon = RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT;
const CheckIcon = RESIDENTS_SVG_COMPONENTS.CHECK;
const EmptySoldIcon = RESIDENTS_SVG_COMPONENTS.EMPTY_SOLD;

type MarketplaceFollowingNavigationProp = StackNavigationProp<RootStackParamList, 'MarketplaceFollowing'>;

const HP = REDESIGN_SPACING.horizontalPadding;
const MAX_CONTENT_WIDTH = 1200;

export const MarketplaceFollowingScreen: React.FC = () => {
  const { layoutMode } = useResponsive();
  if (layoutMode === 'desktop') return <DesktopFollowingView />;
  return <MobileFollowingView />;
};

function useFollowingData() {
  const navigation = useNavigation<MarketplaceFollowingNavigationProp>();

  const {
    data: followingUsers = [],
    isLoading,
    refetch,
  } = useGetFollowingQuery();

  const [toggleFollow] = useToggleFollowMutation();

  const handleFollowToggle = useCallback(async (userId: string) => {
    try { await toggleFollow(userId).unwrap(); } catch (error) { console.error('Failed to toggle follow:', error); }
  }, [toggleFollow]);

  const handleProfilePress = useCallback((userId: string) => {
    navigation.navigate('SellerProfile', { sellerId: userId });
  }, [navigation]);

  return { followingUsers, isLoading, refetch, handleFollowToggle, handleProfilePress };
}

const FollowingContent: React.FC<{
  users: FollowingUser[];
  isLoading: boolean;
  onFollowToggle: (userId: string) => void;
  onProfilePress: (userId: string) => void;
}> = ({ users, isLoading, onFollowToggle, onProfilePress }) => {
  const { colors } = useThemeColors();
  return (
    <View style={styles.contentGap}>
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={PRIMITIVE_COLORS.resioBlue} />
        </View>
      ) : users.length > 0 ? (
        users.map((user) => (
          <FollowingGlassCard
            key={user.id}
            user={user}
            onFollowToggle={() => onFollowToggle(user.id)}
            onProfilePress={() => onProfilePress(user.id)}
          />
        ))
      ) : (
        <View style={styles.emptyContainer}>
          <EmptySoldIcon width={60} height={60} color={colors.text.muted} />
          <Text style={[styles.emptyTitle, { color: colors.text.secondary }]}>Not following anyone yet</Text>
          <Text style={[styles.emptySubtitle, { color: colors.text.dim }]}>Follow sellers to see their new listings</Text>
        </View>
      )}
    </View>
  );
};

const FollowingGlassCard: React.FC<{
  user: FollowingUser;
  onFollowToggle: () => void;
  onProfilePress: () => void;
}> = ({ user, onFollowToggle, onProfilePress }) => {
  const { colors } = useThemeColors();
  const radius = useRadius();
  const space = useSpacing();
  // The name row is bare text + chevron: a padded box that gives the row tint
  // a shape while the content sits exactly where it did (the 8pt marginTop is
  // split between margin and padding).
  const nameRowBox = {
    paddingHorizontal: space.sm,
    marginHorizontal: -space.sm,
    paddingVertical: space.xs,
    marginTop: space.sm - space.xs,
    marginBottom: -space.xs,
    borderRadius: radius.sm,
  };
  return (
    <GlassCard borderRadius={16} padding={16}>
      <View style={styles.userTopRow}>
        <UserAvatar initials={user.initials} avatarUrl={user.avatarUrl} size="large" />
        <Hoverable
          hover={user.isFollowing ? 'row' : 'button'}
          style={[styles.followButton, user.isFollowing && styles.followingButton]}
          onPress={onFollowToggle}
        >
          {user.isFollowing && <CheckIcon width={18} height={18} color={PRIMITIVE_COLORS.resioBlueLight} />}
          <Text style={[styles.followButtonText, { color: colors.text.primary }, user.isFollowing && styles.followingButtonText]}>
            {user.isFollowing ? 'Following' : 'Follow'}
          </Text>
        </Hoverable>
      </View>
      <Hoverable hover="row" style={[styles.userBottomRow, nameRowBox]} onPress={onProfilePress}>
        <Text style={[styles.userName, { color: colors.text.primary }]} numberOfLines={1}>{user.name}</Text>
        <View style={styles.navContainer}>
          {user.hasNewListing && <View style={styles.newListingDot} />}
          <ChevronRightIcon width={18} height={18} color={colors.text.dimmer} />
        </View>
      </Hoverable>
    </GlassCard>
  );
};

const DesktopFollowingView: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation();
  const { followingUsers, isLoading, refetch, handleFollowToggle, handleProfilePress } = useFollowingData();
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => { setRefreshing(true); await refetch(); setRefreshing(false); }, [refetch]);
  const handleBack = useCallback(() => navigation.goBack(), [navigation]);

  return (
    <ScrollView
      style={styles.desktopContainer}
      contentContainerStyle={styles.desktopScrollContent}
      showsVerticalScrollIndicator={false}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.text.primary} />}
    >
      <View style={styles.desktopCentered}>
        <BackLink label="Back to marketplace" onPress={handleBack} color={PRIMITIVE_COLORS.resioBlue} />
        <PageHeader title="Following" />
        <FollowingContent users={followingUsers} isLoading={isLoading} onFollowToggle={handleFollowToggle} onProfilePress={handleProfilePress} />
      </View>
    </ScrollView>
  );
};

const MobileFollowingView: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation();
  const { followingUsers, isLoading, refetch, handleFollowToggle, handleProfilePress } = useFollowingData();
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => { setRefreshing(true); await refetch(); setRefreshing(false); }, [refetch]);
  const handleBack = useCallback(() => navigation.goBack(), [navigation]);

  return (
    <GradientBackground>
      <StatusBar barStyle="light-content" />
      <BaseResidentMobileScreen screenName="Marketplace" darkBackground headerVariant="gradient">
        <ScrollView
          style={styles.mobileScroll}
          contentContainerStyle={styles.mobileScrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.text.primary} />}
        >
          <BackLink label="Back to marketplace" onPress={handleBack} color={PRIMITIVE_COLORS.resioBlue} />
          <PageHeader title="Following" />
          <FollowingContent users={followingUsers} isLoading={isLoading} onFollowToggle={handleFollowToggle} onProfilePress={handleProfilePress} />
        </ScrollView>
      </BaseResidentMobileScreen>
    </GradientBackground>
  );
};

const styles = StyleSheet.create({
  desktopContainer: { flex: 1, backgroundColor: 'transparent' },
  desktopScrollContent: { padding: HP, paddingBottom: 60, alignItems: 'center' },
  desktopCentered: { width: '100%', maxWidth: MAX_CONTENT_WIDTH, gap: 8 },
  mobileScroll: { flex: 1 },
  mobileScrollContent: { paddingHorizontal: HP, paddingBottom: 40, gap: 8 },
  contentGap: { gap: 12 },
  loadingContainer: { paddingVertical: 60, alignItems: 'center', justifyContent: 'center' },
  emptyContainer: { alignItems: 'center', gap: 12, paddingVertical: 40 },
  emptyTitle: { ...REDESIGN_TYPOGRAPHY.cardTitleLarge, textAlign: 'center' },
  emptySubtitle: { ...REDESIGN_TYPOGRAPHY.body, textAlign: 'center', maxWidth: 280 },
  userTopRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  followButton: { backgroundColor: PRIMITIVE_COLORS.resioBlue, borderRadius: 8, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingHorizontal: 16, paddingVertical: 8 },
  followingButton: { backgroundColor: 'transparent', borderWidth: 1, borderColor: PRIMITIVE_COLORS.resioBlue },
  followButtonText: { ...REDESIGN_TYPOGRAPHY.tagFilter },
  followingButtonText: { color: PRIMITIVE_COLORS.resioBlueLight },
  userBottomRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', gap: 20, marginTop: 8 },
  userName: { flex: 1, ...REDESIGN_TYPOGRAPHY.cardTitle },
  navContainer: { flexDirection: 'row', alignItems: 'center', gap: 2 },
  newListingDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: PRIMITIVE_COLORS.warningOrange },
});
